import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useLazyQuery } from '@apollo/client';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import GenericDrawer from '@components/generic-drawer/GenericDrawer';
import Loader from '@components/loader/Loader';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import DynamicForm from '@components/_old/dynamicForm/DynamicForm';
import { DynamicFormInputType } from '@components/_old/dynamicForm';
import { IAbstractRecord } from '@models';
import { DEFAULT_ERROR_TEXT } from '@utils';
import {
  POLICY_EXCEL_HEADER_STYLE,
  downloadExcelFile,
} from 'src/pages/policy-management/excel/new-download-json-excel-service';
import {
  exportAgencyVignetteRange,
  getExportVignetteRangesLookups,
} from './queries';
import {
  ALL_OPTION,
  EXPORT_FILE_NAME,
  MULTI_CHOICE_FIELDS,
  excelHeaders,
  getInputs,
} from './content';
import {
  mapItemsToExcelData,
  mapLookupsToLovs,
  normalizeAllSelection,
  toFilterValue,
  toYearFilterValue,
} from './utils';

interface IExportVignetteRangesDrawerProps {
  open: boolean;
  onClose: () => void;
}

const ExportVignetteRangesDrawer: React.FC<
  IExportVignetteRangesDrawerProps
> = ({ open, onClose }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [formDisabled, setFormDisabled] = useState<boolean>(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();
  const [lovs, setLovs] = useState<Record<string, Record<string, string>>>(
    {}
  );

  const selections = useRef<Record<string, string[]>>(
    Object.fromEntries(MULTI_CHOICE_FIELDS.map((field) => [field, [ALL_OPTION]]))
  );

  const [lookupsQuery] = useLazyQuery(getExportVignetteRangesLookups(), {
    fetchPolicy: 'no-cache',
  });
  const [exportQuery] = useLazyQuery(exportAgencyVignetteRange(), {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    lookupsQuery()
      .then(({ data, error }) => {
        if (error) {
          throw error;
        }
        setLovs(mapLookupsToLovs(data));
      })
      .catch(() => {
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
      })
      .finally(() => setLoading(false));
  }, []);

  const inputsForm = useMemo(
    (): Record<string, DynamicFormInputType> => getInputs(lovs),
    [lovs]
  );

  const onFieldChange = (
    fieldName: string,
    value: unknown,
    values: Record<string, any>
  ) => {
    if (!MULTI_CHOICE_FIELDS.includes(fieldName)) {
      return;
    }
    const next = (value as string[]) || [];
    const allOptionsCount = Object.keys(lovs[fieldName] || {}).length + 1;
    values[fieldName] =
      next.length === allOptionsCount
        ? [ALL_OPTION]
        : normalizeAllSelection(selections.current[fieldName], next);
    selections.current[fieldName] = values[fieldName];
  };

  const submitForm = async (values: IAbstractRecord) => {
    setFormDisabled(true);
    setSubmitButtonState('loading');

    try {
      const { data, error } = await exportQuery({
        variables: {
          SelectedAgencyID: toFilterValue(values.agency),
          SelectedRangeYear: toYearFilterValue(values.rangeYear),
          SelectedRangeStatus: toFilterValue(values.rangeStatus),
        },
      });

      if (error) {
        throw error;
      }

      const items: IAbstractRecord[] =
        data?.Insurance?.queries?.exportAgencyVignetteRange || [];

      if (items.length === 0) {
        setSubmitButtonState(undefined);
        toast.error(
          <ToastErrorMessage>
            No vignette ranges found for the selected criteria
          </ToastErrorMessage>
        );
        return;
      }

      downloadExcelFile({
        data: mapItemsToExcelData(items, lovs.rangeStatus || {}),
        filename: EXPORT_FILE_NAME,
        headers: excelHeaders,
        headerStyle: POLICY_EXCEL_HEADER_STYLE,
      });
      toast.success(
        <ToastSuccessMessage>
          Vignette ranges successfully exported
        </ToastSuccessMessage>
      );
      setSubmitButtonState('success');
      setTimeout(() => onClose(), 500);
    } catch {
      setSubmitButtonState(undefined);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    } finally {
      setFormDisabled(false);
    }
  };

  return (
    <GenericDrawer
      title={'Export Vignette Ranges'}
      onClose={() => onClose()}
      isOpen={open}
    >
      {loading ? (
        <Loader />
      ) : (
        <DynamicForm
          inputs={inputsForm}
          onChange={onFieldChange}
          onSubmit={(v) => submitForm(v)}
          buttonText={'Submit'}
          submitButtonState={submitButtonState}
          disableForm={formDisabled}
        />
      )}
    </GenericDrawer>
  );
};

export default ExportVignetteRangesDrawer;
